import { BuildPresentationProps } from '@features/lci-report/subdomains/visitation/utils/build-presentation';
import { Slide } from '@features/powerpoint';
import { salesAttributionTitleSlide } from '@features/lci-report/slides/sales-attribution/sales-attribution-title-slide';
import { contributionMetricsSummarySlide } from './contribution-metrics-summary-chart/contribution-metrics-summary-slide';
import { salesLiftSummaryTableSlide } from '@features/lci-report/slides/sales-attribution/sales-lift-summary-table-slide';
import { salesLiftSummaryChartSlide } from '@features/lci-report/slides/sales-attribution/sales-lift-summary-chart-slide';
import { rsaDimensionsTableSlide } from './rsa-dimensions-table-slide';
import { Lci3DimensionTableData } from '@features/lci-report/models/lci3-types';

const RSA_TOP_PERFORMER_SLIDE_HEADER = 'Top Performing Dimensions (Sales)';
const RSA_BOTTOM_PERFORMER_SLIDE_HEADER =
  'Bottom Performing Dimensions (Sales)';

const salesAttributionTitleSlideFromProps = (
  props: BuildPresentationProps
): Slide[] =>
  props.customFields.includedSlides.salesAttribution.titleSlide
    ? [salesAttributionTitleSlide]
    : [];

const rsaDimensionSlidesFromProps = (
  props: BuildPresentationProps,
  slideMaster: string,
  dimensionType: keyof Lci3DimensionTableData,
  slideHeader: string
) => {
  if (!props.customFields.includedSlides.salesAttribution.dimensions) return [];

  const dimensionData = props.dimensionTableData[dimensionType];

  return (dimensionData?.length ?? 0) > 0 && props.salesSummary !== null
    ? [
        rsaDimensionsTableSlide({
          slideMaster,
          subdimensions: dimensionData ?? [],
          overallSalesMetrics: props.salesSummary,
          overallVisitMetrics: props.visitMetrics,
          slideHeader,
        }),
      ]
    : [];
};

const salesLiftSummaryChartSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  return props.customFields.includedSlides.salesAttribution.summary &&
    props.salesSummary !== null
    ? [
        salesLiftSummaryChartSlide({
          slideMaster,
          overallSalesMetrics: props.salesSummary,
          iroas: props.calculatedFields.incrementalReturnOnAdSpend ?? 0,
        }),
      ]
    : [];
};

const salesLiftSummaryTableSlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
) => {
  return props.customFields.includedSlides.salesAttribution.summaryTable &&
    props.salesSummary !== null
    ? [
        salesLiftSummaryTableSlide({
          slideMaster,
          overallSalesMetrics: props.salesSummary,
          keyTakeaways: props.customFields.rsaSummaryTable,
          rsaOverallResults: props.customFields.rsaOverallResults,
        }),
      ]
    : [];
};

const contributionMetricsSummarySlideFromProps = (
  props: BuildPresentationProps,
  slideMaster: string
) => {
  return props.salesSummary !== null &&
    props.customFields.includedSlides.salesAttribution.contributionMetrics
    ? [
        contributionMetricsSummarySlide({
          slideMaster,
          overallSalesMetrics: props.salesSummary,
          metric: 'total',
          title: 'Combined',
          keyTakeaways: props.customFields.cmOverall,
        }),
        contributionMetricsSummarySlide({
          slideMaster,
          overallSalesMetrics: props.salesSummary,
          metric: 'inStore',
          title: 'In-Store',
          keyTakeaways: props.customFields.cmInStore,
        }),
        contributionMetricsSummarySlide({
          slideMaster,
          overallSalesMetrics: props.salesSummary,
          metric: 'online',
          title: 'Online',
          keyTakeaways: props.customFields.cmOnline,
        }),
      ]
    : [];
};

export const salesAttributionSlides = (
  props: BuildPresentationProps,
  slideMaster: string
): Slide[] => {
  if (!props.salesSummary) return [];

  const topPerformerSlideHeader =
    props.customFields.dimensions.Top.salesTableHeader.length > 0
      ? props.customFields.dimensions.Top.salesTableHeader
      : RSA_TOP_PERFORMER_SLIDE_HEADER;

  const bottomPerformerSlideHeader =
    props.customFields.dimensions.Bottom.salesTableHeader.length > 0
      ? props.customFields.dimensions.Bottom.salesTableHeader
      : RSA_BOTTOM_PERFORMER_SLIDE_HEADER;

  return [
    salesAttributionTitleSlideFromProps(props),
    salesLiftSummaryChartSlideFromProps(props, slideMaster),
    salesLiftSummaryTableSlideFromProps(props, slideMaster),
    ...contributionMetricsSummarySlideFromProps(props, slideMaster),
    rsaDimensionSlidesFromProps(
      props,
      slideMaster,
      'Top',
      topPerformerSlideHeader
    ),
    rsaDimensionSlidesFromProps(
      props,
      slideMaster,
      'Bottom',
      bottomPerformerSlideHeader
    ),
  ].flat();
};
